/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react'
import {
  Box,
  Grid,
  Button,
  CircularProgress,
  Grow,
  Alert,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
  SvgIcon,
  IconButton,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import ListService from 'api/services/Lists'
import CustomDialog from 'shared/CustomDialog'
import Listing from './Listing'
import ListingTabletView from './ListingTabletView'
import ListName from './ListName'
import { ListProperty, UserFavoritesProperty } from 'types'
import { AdminPropertyAdWithList } from './types'
import BasePagination from 'components/shared/BasePagination'
import ListInstructions from 'components/Lists/ListInstructions'
import TreePNG from 'assets/img/land-single-tree.png'
import SmallBuildingPNG from 'assets/img/small-building.png'
import BigBuildingPNG from 'assets/img/big-building.png'
import { SortingCategoryEnum, SortingOrderEnum } from 'types/enums'
import { ReactComponent as DotsOnlySvg } from 'assets/svg/new-icons/dots-only.svg'
import ListNameSM from './ListNameSM'
import ListingLaptop from './Laptop/Listing'
import { useQueryClient } from 'react-query'
import usePageView from 'hooks/usePageView'

const List = () => {
  const { sendPageView } = usePageView({ runOnMount: false })
  const { t } = useTranslation()

  const { listId } = useParams()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [page, setPage] = useState<number>(0)
  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1)
  }

  const PER_PAGE = 30

  const [sortingField, setSortingField] = React.useState<string>('')

  const list = ListService.hooks.useList(Number(listId), {
    enabled: !!listId && !!Number(listId),
    onSettled: () => {
      sendPageView()
    },
  })

  const NOT_MATCHED =
    !listId || !Number(listId) || (list.isSuccess && !list.data)
  const MATCHED = list.isSuccess && !!list.data

  const payload: Partial<{
    sortingOrder: SortingOrderEnum
    sortingCategory: SortingCategoryEnum
  }> = {}
  if (sortingField) {
    const split = sortingField.split('#')
    payload.sortingOrder = split[0] as SortingOrderEnum
    payload.sortingCategory = split[1] as SortingCategoryEnum
  }

  const listPropertyAds = ListService.hooks.useListPropertyAds<
    AdminPropertyAdWithList[]
  >(Number(listId), page, PER_PAGE, payload, {
    enabled: MATCHED,
    select: (data) => {
      if (data && MATCHED) {
        return data.reduce<AdminPropertyAdWithList[]>((acc, pAd) => {
          const favPropertyAd = list.data?.userFavorites.find(
            (fav) => fav.entityId === pAd.alternativeId
          )
          if (favPropertyAd) {
            return [
              ...acc,
              {
                ...JSON.parse(JSON.stringify(pAd)),
                favorite: { ...favPropertyAd } as UserFavoritesProperty,
              },
            ]
          }
          return acc
        }, [])
      }
      return [] as AdminPropertyAdWithList[]
    },
  })

  const handleDeleteListing = () => {
    if (Number(listId)) {
      list.refetch()
      listPropertyAds.remove()
    }
  }

  const deleteUserFavoritesList =
    ListService.hooks.useDeleteUserFavoritesListMutation({
      onSuccess: () => {
        navigate('/lists')
      },
    })

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleSubmitDelete = () => {
    deleteUserFavoritesList.mutate({
      listId: listId,
    })
    handleCloseDeleteDialog()
  }

  const handleUpdateFavorites = (
    name: string | null,
    notes: string | null,
    propertyAdAlternativeId: string
  ) => {
    if (listPropertyAds.data) {
      const copyAds = JSON.parse(JSON.stringify(listPropertyAds.data))
      const matchedIndex = listPropertyAds.data.findIndex(
        (item) => item.alternativeId === propertyAdAlternativeId
      )
      if (matchedIndex !== -1 && copyAds[matchedIndex].favorite) {
        const newFavoriteInfo = {
          ...copyAds[matchedIndex].favorite,
          name,
          notes,
        }
        const updatedPropertyAd: AdminPropertyAdWithList = JSON.parse(
          JSON.stringify(listPropertyAds.data[matchedIndex])
        )
        updatedPropertyAd.favorite = newFavoriteInfo
        copyAds.splice(matchedIndex, 1, updatedPropertyAd)
        queryClient.setQueryData(
          [ListService.keys.QueryKeys.listSingle, Number(listId)],
          (prev: ListProperty) => {
            const matchFavorite = prev?.userFavorites?.find(
              (item) => item.entityId === propertyAdAlternativeId
            )
            if (matchFavorite) {
              matchFavorite.name = name
              matchFavorite.notes = notes
            }
            return prev
          }
        )
        queryClient.setQueryData(
          [
            ListService.keys.QueryKeys.listPropertyAds,
            Number(listId),
            page,
            PER_PAGE,
            { ...payload },
          ],
          copyAds
        )
      }
    }
  }
  const listingInstructions = [
    { number: 1, text: t('listInstructions.searchAndFind'), icon: TreePNG },
    { number: 2, text: t('listInstructions.clickOn'), icon: SmallBuildingPNG },
    {
      number: 3,
      text: t('listInstructions.addPropertyToList'),
      icon: BigBuildingPNG,
    },
  ]

  const SORTING_OPTIONS = [
    {
      label: 'listDetailView.actions.dateAdded',
      value: 'DESC#DATE_CREATED',
    },
    {
      label: 'listDetailView.actions.priceAscending',
      value: 'ASC#SALES_PRICE',
    },
    {
      label: 'listDetailView.actions.priceDescending',
      value: 'DESC#SALES_PRICE',
    },
    {
      label: 'listDetailView.actions.pricePerSqMAscending',
      value: 'ASC#SALES_PRICE_PER_SQR_METER',
    },
    {
      label: 'listDetailView.actions.pricePerSqMDescending',
      value: 'DESC#SALES_PRICE_PER_SQR_METER',
    },
  ]

  const handleUpdateSorting = (e) => {
    setSortingField(e.target.value)
    if (page > 0) {
      setPage(0)
    }
  }

  const onUpdateListName = (updatedList: ListProperty) => {
    queryClient.setQueryData(
      [ListService.keys.QueryKeys.listSingle, updatedList.id],
      updatedList
    )
  }

  const theme = useTheme()

  // const matchesSM = useMediaQuery(theme.breakpoints.down('sm'))
  const matchesSM = useMediaQuery(theme.breakpoints.down(600))
  // const matchesTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const matchesLaptop = useMediaQuery(theme.breakpoints.between(901, 1439))
  const matchesDesktop = useMediaQuery(theme.breakpoints.up(1440))

  return (
    <Box
      mx="auto"
      sx={{
        mt: '42px',
        // maxWidth: { xs: '360px', sm: '360px', md: '741px', lg: '1264px' },
        width: '100%',
        [theme.breakpoints.down('xl')]: {
          maxWidth: '1264px',
        },
        [theme.breakpoints.down(900)]: {
          maxWidth: '741px',
          mt: 0,
        },
        [theme.breakpoints.down('sm')]: {
          maxWidth: '360px',
          mt: 0,
        },
      }}
      key={listId}
    >
      <Grid
        container
        alignItems="center"
        rowGap="10px"
        justifyContent={{
          xs: 'flex-end',
          sm: 'space-evenly',
          md: 'space-between',
        }}
        direction="row"
      >
        <Grid
          item
          sx={{
            display: { xs: 'none' },
            [theme.breakpoints.up(900)]: {
              display: 'initial',
            },
          }}
        >
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/lists`}
            sx={{
              textTransform: 'none',
              bgcolor: 'common.white',
              border: '0.2px solid #EBEBEB',
              boxShadow: '3px 3px 8px rgba(9, 9, 9, 0.3)',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 700,
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'common.white',
                border: '0.2px solid #EBEBEB',
              },
            }}
          >
            {t('listDetailView.backToLists')}
          </Button>
        </Grid>

        {matchesSM || matchesTablet ? null : (
          <Grid item>
            <Button
              onClick={() => handleOpenDeleteDialog()}
              sx={{
                textTransform: 'none',
                bgcolor: 'common.white',
                border: '0.2px solid #EBEBEB',
                boxShadow: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 400,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'common.white',
                  border: '0.2px solid #EBEBEB',
                },
              }}
            >
              {t('listDetailView.actions.deleteList')}
            </Button>
          </Grid>
        )}
      </Grid>
      {NOT_MATCHED ? (
        <Typography sx={{ mt: '25px' }}>{`Η λίστα δεν βρέθηκε`}</Typography>
      ) : null}
      <Grow in={list.isError || listPropertyAds.isError} unmountOnExit>
        <Alert
          severity="error"
          sx={{ maxWidth: '100%', mx: 'auto', mt: '25px' }}
        >
          {t('error.unspecific')}
        </Alert>
      </Grow>
      {MATCHED ? (
        <>
          <Grid
            container
            alignItems="center"
            justifyContent={'center'}
            sx={{
              // mt: 0,
              mt: { xs: 0, md: '25px' },
              // display: { md: 'none' },
              [theme.breakpoints.up(900)]: {
                display: 'none',
                // mt: '25px',
              },
              // [theme.breakpoints.between('sm', 900)]: {
              //   mt: 0,
              // },
            }}
          >
            <Grid item xs={12}>
              <Grid
                container
                justifyContent={'center'}
                wrap="nowrap"
                sx={{ position: 'relative' }}
              >
                <Grid item xs="auto">
                  <ListNameSM list={list.data} />
                </Grid>
                {matchesSM || matchesTablet ? (
                  <Grid item alignSelf={'flex-end'}>
                    <IconButton
                      onClick={() => handleOpenDeleteDialog()}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: '34px',
                        height: '34px',
                        border: '0.5px solid #EBEBEB',
                        boxShadow: '3px 3px 5px 0px #00000040',
                        '&:hover': {
                          bgcolor: 'initial',
                        },
                      }}
                    >
                      <SvgIcon
                        component={DotsOnlySvg}
                        inheritViewBox
                        sx={{
                          color: '#ffffff',
                        }}
                      />
                    </IconButton>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            rowGap="10px"
            justifyContent={{
              xs: 'flex-end',
              // sm: 'flex-end',
              // md: 'space-between',
            }}
            sx={{
              mt: {
                xs: '13px',
              },
              [theme.breakpoints.up(900)]: {
                justifyContent: 'space-between',
              },
              [theme.breakpoints.between('sm', 900)]: {
                mt: '18px',
              },
            }}
          >
            {matchesSM || matchesTablet ? null : (
              <Grid item>
                <ListName list={list.data} onUpdate={onUpdateListName} />
              </Grid>
            )}

            {list?.data?.userFavoritesCount !== 0 ? (
              <Grid item sx={{ alignSelf: 'flex-end' }}>
                <TextField
                  select
                  onChange={handleUpdateSorting}
                  size="small"
                  defaultValue=""
                  SelectProps={{
                    displayEmpty: true,
                  }}
                  InputProps={{
                    sx: { outline: 'none', border: 'none' },
                  }}
                  sx={{
                    backgroundColor: 'white',
                    boxShadow: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 400,
                    '.MuiOutlinedInput-notchedOutline': {
                      border: 0,
                    },
                  }}
                >
                  <MenuItem disabled value={''}>
                    {t('listDetailView.actions.sort')}
                  </MenuItem>
                  {SORTING_OPTIONS.map((item) => {
                    return (
                      <MenuItem key={item?.value} value={item?.value}>
                        {t(item.label)}
                      </MenuItem>
                    )
                  })}
                </TextField>
              </Grid>
            ) : (
              ''
            )}
          </Grid>
        </>
      ) : null}
      {/* {userFavoritesList.isSuccess && userFavoritesListPropertyAds.isLoading ? ( */}
      {list.isSuccess && listPropertyAds.isLoading ? (
        <CircularProgress
          size={50}
          sx={{ display: 'block', mx: 'auto', mt: '30px' }}
        />
      ) : null}

      <Grow
        in={
          !list.data?.userFavorites?.length &&
          listPropertyAds.isSuccess &&
          list.isSuccess
        }
        unmountOnExit
      >
        <div>
          <ListInstructions
            instructions={listingInstructions}
            titleList={false}
          />
        </div>
      </Grow>
      <Grow in={listPropertyAds.isSuccess && !!listPropertyAds.data?.length}>
        <Box sx={{}}>
          {listPropertyAds.data?.length ? (
            <Grid
              container
              justifyContent={'center'}
              spacing={0}
              sx={{
                rowGap: { xs: '8px', sm: '18px' },
                // mt: { xs: '14px', md: '50px' },
                [theme.breakpoints.up(1440)]: {
                  mt: '50px',
                },
                [theme.breakpoints.between(901, 1439)]: {
                  rowGap: '18px',
                  mt: '50px',
                },
                [theme.breakpoints.between('sm', 900)]: {
                  mt: '30px',
                },
                [theme.breakpoints.down('sm')]: {
                  mt: '14px',
                },
              }}
            >
              {listPropertyAds.data.map((item) => (
                <Grid
                  item
                  key={item.id}
                  sx={{
                    flexGrow: { xs: 0, sm: 1 },
                    [theme.breakpoints.between(901, 1439)]: {
                      flexGrow: 1,
                    },
                  }}
                >
                  {matchesLaptop ? (
                    <ListingLaptop
                      matchesLaptop={matchesLaptop}
                      matchesSM={matchesSM}
                      matchesTablet={matchesTablet}
                      propertyAd={item as AdminPropertyAdWithList}
                      onDelete={handleDeleteListing}
                      listId={list.data?.id || null}
                      onUpdate={handleUpdateFavorites}
                    />
                  ) : null}
                  {matchesTablet ? (
                    <ListingTabletView
                      matchesLaptop={matchesLaptop}
                      matchesSM={matchesSM}
                      matchesTablet={matchesTablet}
                      propertyAd={item as AdminPropertyAdWithList}
                      onDelete={handleDeleteListing}
                      listId={list.data?.id || null}
                      onUpdate={handleUpdateFavorites}
                    />
                  ) : null}

                  {matchesDesktop || matchesSM ? (
                    <Listing
                      matchesLaptop={matchesLaptop}
                      matchesSM={matchesSM}
                      matchesTablet={matchesTablet}
                      propertyAd={item as AdminPropertyAdWithList}
                      onDelete={handleDeleteListing}
                      listId={list.data?.id || null}
                      onUpdate={handleUpdateFavorites}
                    />
                  ) : null}
                </Grid>
              ))}
            </Grid>
          ) : null}
          {list.data && Number(list.data?.userFavoritesCount) >= PER_PAGE ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: { xs: '50px', md: '138px' },
              }}
            >
              <BasePagination
                count={Math.ceil(
                  (list.data?.userFavoritesCount ?? 0) / PER_PAGE
                )}
                page={page + 1}
                onChange={handleChangePage}
              />
            </Box>
          ) : null}
        </Box>
      </Grow>
      <CustomDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onSubmit={handleSubmitDelete}
        titleLabel={t('listDetailView.actions.deleteList')}
        subtitleLabel={`${t('deleteListPopUp.deleteMessage')}
          ${list?.data?.name}${'.'} ${t(
          'deleteListPopUp.deleteAreYouSure'
        )}${';'}`}
        cancelLabel={t(`ListPage.deleteDialog.no`)}
        submitLabel={t(`ListPage.deleteDialog.yes`)}
      />
    </Box>
  )
}

export default List
